/* eslint-disable react/jsx-filename-extension */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import * as XLSX from 'xlsx';

import StepsContainer from '../../common/containers/StepsContainer';
import SheetUploadPrompt from '../../common/excel/SheetUploadPrompt';

import SheetHeaderMatcher from '../../common/excel/SheetHeaderMatcher';
import MaterialSheetSelector from './MaterialSheetSelector';
import MaterialUploadPreview from './MaterialUploadPreview';

const defaultHeaders = [
  { key: 'name', title: 'Name' },
  { key: 'description', title: 'Description' },
  { key: 'partNumber', title: 'Part Number' },
  { key: 'supplier', title: 'Supplier' },
  { key: 'cost', title: 'Cost' },
  { key: 'markup', title: 'Markup' },
  { key: 'units', title: 'Units' },
  { key: 'quantity', title: 'Quantity' },
];

export default function MaterialUpload({
  visible,
  uploadFile,
  onFileChange,
  currentStep,
  divisionIds,
  divisionFilter,
  onDivisionChange,
  onMassUploadDataChanged,
}) {
  const [selectedSheetName, setSelectedSheetName] = useState();
  const [headerMapping, setHeaderMapping] = useState({});

  const sheets = useMemo(() => {
    if (!uploadFile) return {};
    const { Sheets = {} } = uploadFile;
    return Sheets;
  }, [uploadFile]);

  const selectedSheet = useMemo(() => {
    if (!sheets || !selectedSheetName) return [];
    const { [selectedSheetName]: fullSheet = {} } = sheets;
    return XLSX.utils.sheet_to_json(fullSheet, { defval: '' });
  }, [sheets, selectedSheetName]);

  useEffect(() => {
    if (!uploadFile) return;
    const { SheetNames = [] } = uploadFile;
    if (!selectedSheetName || (SheetNames.length === 1 && selectedSheetName !== SheetNames[0])) {
      setSelectedSheetName(SheetNames[0]);
    }
  }, [selectedSheetName, uploadFile]);

  useEffect(() => {
    if (visible) return;
    setSelectedSheetName();
    setHeaderMapping({});
  }, [visible]);

  if (!uploadFile) {
    return (
      <div style={{ padding: 24 }}>
        <SheetUploadPrompt onFileChange={onFileChange} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <StepsContainer
        currentStep={currentStep}
        steps={[
          {
            title: 'Select Sheet',
            content: (
              <MaterialSheetSelector
                workbook={uploadFile}
                onSheetChanged={setSelectedSheetName}
                divisionIds={divisionIds}
                divisionFilter={divisionFilter}
                onDivisionsChanged={onDivisionChange}
              />
            ),
          },
          {
            title: 'Select Headers',
            content: (
              <SheetHeaderMatcher
                headerOptions={defaultHeaders}
                selectedSheet={selectedSheet}
                headerMapping={headerMapping}
                setHeaderMapping={setHeaderMapping}
              />
            ),
          },
          {
            title: 'Preview',
            content: (
              <MaterialUploadPreview
                selectedSheet={selectedSheet}
                headerMapping={headerMapping}
                onMassUploadDataChanged={onMassUploadDataChanged}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

MaterialUpload.propTypes = {
  visible: PropTypes.bool,
  uploadFile: PropTypes.shape({
    Sheets: PropTypes.shape({}),
    SheetNames: PropTypes.arrayOf(PropTypes.string),
  }),
  onFileChange: PropTypes.func.isRequired,
  currentStep: PropTypes.number,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  divisionFilter: PropTypes.instanceOf(Set),
  onDivisionChange: PropTypes.func.isRequired,
  onMassUploadDataChanged: PropTypes.func.isRequired,
};

MaterialUpload.defaultProps = {
  visible: false,
  uploadFile: null,
  currentStep: 0,
  divisionIds: [],
  divisionFilter: undefined,
};
